/**
 * Splits a TES reply into what the chat shows as cards and sections — presentation only, the
 * reply text itself is unchanged and anything that doesn't match falls back to plain markdown.
 *
 * - "Here's the strategy as I read it … **Read as:** …" and "**What was actually tested**" lists
 *   become one strategy card (rows + what's not included yet).
 * - Inline bold section titles ("**Why it failed**", "**Next step**") become headings; bold labels
 *   that end in a colon ("**Trend filter:**") stay inline.
 */

export interface StrategyRdCoachStrategyRow {
  key: string;
  value: string;
}

export interface StrategyRdCoachStrategySummary {
  /** "XAUUSD 15m · the last 6 months (180 days)". */
  market: string | null;
  rows: StrategyRdCoachStrategyRow[];
  /** Parts left at "none": "trading costs", "daily limits", "trade management", "filters". */
  notIncluded: string[];
  /** Only TES's reading of the ask — nothing has been tested yet. */
  draft: boolean;
}

export interface StrategyRdCoachReplySection {
  heading: string | null;
  body: string;
}

export interface StrategyRdCoachReplyParts {
  sections: StrategyRdCoachReplySection[];
  strategy: StrategyRdCoachStrategySummary | null;
}

const KEY_NAMES: Array<[RegExp, string]> = [
  [/^filters?\b/i, "Filters"],
  [/^costs?\b/i, "Costs"],
  [/^entry\b/i, "Entry"],
  [/^setup\b/i, "Setup"],
  [/^sessions?\b/i, "Sessions"],
  [/^stop\b/i, "Stop"],
  [/^target\b/i, "Target"],
  [/^manage\b/i, "Manage"],
  [/^daily limits?\b/i, "Daily limits"],
  [/^account\b/i, "Account"],
  [/^starting balance\b/i, "Starting balance"],
];

const NOT_INCLUDED: Record<string, string> = {
  Filters: "filters",
  Manage: "trade management",
  "Daily limits": "daily limits",
  Costs: "trading costs",
};

const SIDE_LABELS: Record<string, string> = {
  both: "Both sides",
  "both sides": "Both sides",
  long: "Longs",
  longs: "Longs",
  short: "Shorts",
  shorts: "Shorts",
};

function parseRow(line: string): { key: string; value: string } | null {
  const m = line.replace(/\*\*/g, "").match(/^([^:()]+?)\s*(?:\(([^)]*)\))?\s*:\s*(.+)$/);
  if (!m) return null;
  const rawKey = m[1].trim();
  const qualifier = (m[2] || "").trim().toLowerCase();
  let value = m[3].trim();
  const named = KEY_NAMES.find(([re]) => re.test(rawKey));
  const key = named ? named[1] : rawKey;
  if (key === "Entry" && SIDE_LABELS[qualifier]) value = `${SIDE_LABELS[qualifier]} · ${value}`;
  return { key, value };
}

/** "- Market: … - Setup: …" — one item per line, or run together on one line. */
function listItems(block: string): string[] {
  return block
    .split(/(?:^|\n|\s)[-*]\s+(?=\**[A-Z][^:\n]{0,40}?\**\s*(?:\([^)]*\))?\s*:)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** "- <ask> — <how TES read it>" items, one per line or run together. */
function plainItems(block: string): string[] {
  return block
    .split(/(?:^|\n|(?<=[.!?)])\s)[-*]\s+(?=\S)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function summarize(
  items: string[],
  extra: StrategyRdCoachStrategyRow[] = [],
  draft = false
): StrategyRdCoachStrategySummary | null {
  let market: string | null = null;
  const rows: StrategyRdCoachStrategyRow[] = [];
  for (const item of items) {
    const row = parseRow(item);
    if (!row) continue;
    if (/^market$/i.test(row.key)) market = row.value;
    else rows.push(row);
  }
  for (const row of extra) {
    if (!rows.some((r) => r.key === row.key)) rows.push(row);
  }
  if (rows.length < 3) return null;
  const notIncluded: string[] = [];
  const kept = rows.filter((r) => {
    const label = NOT_INCLUDED[r.key];
    if (label && /^none\b/i.test(r.value)) {
      notIncluded.push(label);
      return false;
    }
    return true;
  });
  return { market, rows: kept, notIncluded, draft };
}

/**
 * "**Needs your answer:** - Should …? - Where …? Proposed, not applied yet: …" → a heading, the
 * questions as a list, then the rest as its own paragraph.
 */
function labelledInlineLists(text: string): string {
  return text.replace(
    /\*\*([A-Z][^*\n:]{1,40}):\*\*\s+[-*]\s+([\s\S]*?)(?=\s\*\*[^*\n]{2,40}:?\*\*|\n\n|$)/g,
    (_, label: string, body: string) => {
      const items = plainItems(`- ${body}`);
      const last = items.pop() ?? "";
      const cut = last.search(/[?]\s+(?=[A-Z])/);
      const tail = cut >= 0 ? last.slice(cut + 1).trim() : "";
      items.push(cut >= 0 ? last.slice(0, cut + 1) : last);
      return `\n\n### ${label.trim()}\n\n${items.map((i) => `- ${i}`).join("\n")}\n\n${tail}`;
    }
  );
}

/** "**Why it failed** I checked…" → "### Why it failed\n\nI checked…" (only title-like bold runs). */
export function strategyRdCoachBoldTitlesToHeadings(text: string): string {
  return text
    .replace(/(^|\n|[.:!?]\s+)\*\*([A-Z][^*\n:]{1,48}?)\*\*(?=\s*\n|\s+[A-Z*\-])/g, (_, lead: string, title: string) => {
      if (title.split(/\s+/).length > 6) return `${lead}**${title}**`;
      return `${lead.replace(/\s+$/, "")}\n\n### ${title.trim()}\n\n`;
    })
    .replace(/(### [^\n]+\n\n)[ \t]+/g, "$1")
    .replace(/\s\*\s(?=\*\*)/g, "\n* ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function toSections(text: string): StrategyRdCoachReplySection[] {
  const out: StrategyRdCoachReplySection[] = [];
  let heading: string | null = null;
  let body: string[] = [];
  const flush = () => {
    const b = body.join("\n").trim();
    if (b || heading) out.push({ heading, body: b });
  };
  for (const line of text.split("\n")) {
    const h = line.match(/^#{1,3}\s+(.+)$/);
    if (h) {
      flush();
      heading = h[1].trim();
      body = [];
    } else {
      body.push(line);
    }
  }
  flush();
  return out;
}

export function strategyRdCoachReplyParts(markdown: string): StrategyRdCoachReplyParts {
  let text = markdown || "";
  let readRows: StrategyRdCoachStrategyRow[] = [];
  let readItems: string[] = [];

  // "Here's the strategy as I read it — … accounted for. - Market: … **Read as:** - <ask> — <how TES read it>"
  // (or "Here's how I read it so far. One thing needs your answer … - Market: …").
  const read = text.match(
    /Here'?s (?:the strategy as I read it|how I read it)[^.\n]*\.?\s*([\s\S]*?)\*\*Read as:?\*\*\s*([\s\S]*?)(?=\s\*\*[^*\n]{2,40}:?\*\*|\n\n|$)/i
  );
  if (read) {
    const listAt = read[1].search(/(?:^|\s)[-*]\s+\**[A-Z][^:\n]{0,40}?\**\s*(?:\([^)]*\))?\s*:/);
    const lead = listAt > 0 ? read[1].slice(0, listAt).trim() : "";
    readItems = listItems(listAt >= 0 ? read[1].slice(listAt) : read[1]);
    readRows = readItems.map(parseRow).filter((r): r is StrategyRdCoachStrategyRow => r != null);
    const readAs = plainItems(read[2])
      .map((line) => {
        const dash = line.indexOf(" — ");
        return dash >= 0 ? line.slice(dash + 3).trim() : line;
      })
      .join("\n\n");
    text = text.replace(read[0], `${[lead, readAs].filter(Boolean).join("\n\n")}\n\n`);
  }

  let strategy: StrategyRdCoachStrategySummary | null = null;
  const tested = text.match(/\n?\s*\*\*What was actually tested:?\*\*\s*\n((?:\s*[-*]\s+[^\n]*(?:\n|$))+)/i);
  if (tested) {
    strategy = summarize(listItems(tested[1]), readRows.filter((r) => /^(Account|Starting balance)$/.test(r.key)));
    if (strategy) text = text.replace(tested[0], "\n");
  } else if (readItems.length) {
    strategy = summarize(readItems, [], true);
  }

  // No card to show: keep the reply exactly as written.
  if (!strategy) text = markdown || "";
  return { sections: toSections(strategyRdCoachBoldTitlesToHeadings(labelledInlineLists(text))), strategy };
}
